import { Badge, DataTable, type TanstackReactTable } from "@devhop/ui";
import { useQueries } from "@tanstack/react-query";
import { EditIcon, PlusIcon } from "lucide-react";
import { formatPrice } from "@/shared/lib/format-price";
import { type FeeRuleDTO, feesApi } from "../api/client";

function formatValue(row: FeeRuleDTO): string {
  if (row.feeMode === "percent") {
    return `${Number(row.value).toFixed(2)}%`;
  }
  return formatPrice(Number(row.value), "", {
    appendCurrency: false,
    locale: "en-US",
  });
}

type CategoryRow = {
  categoryId: string;
  categoryName: string;
  marketplace: FeeRuleDTO | null;
  mechanic: FeeRuleDTO | null;
};

type CategoryFeeViewProps = {
  canManage: boolean;
  offset: number;
  limit: number;
  onEdit: (rule: FeeRuleDTO) => void;
  onAddForCategory: (categoryId: string, categoryName: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function CategoryFeeView({
  canManage,
  offset,
  limit,
  onEdit,
  onAddForCategory,
  onPaginationChange,
}: CategoryFeeViewProps) {
  const [categoriesQ, rulesMq, rulesMechQ] = useQueries({
    queries: [
      {
        queryKey: ["fees", "lookup", "categories", "paginated", offset, limit],
        queryFn: () => feesApi.lookupCategories({ limit, skip: offset }),
      },
      {
        queryKey: ["fees", "list", "marketplace_commission", "scope_category"],
        queryFn: () =>
          feesApi.list({
            feeType: "marketplace_commission",
            limit: 500,
            offset: 0,
            filters: [{ field: "scope", op: "eq", value: "category" }],
          }),
      },
      {
        queryKey: ["fees", "list", "mechanic_commission", "scope_category"],
        queryFn: () =>
          feesApi.list({
            feeType: "mechanic_commission",
            limit: 500,
            offset: 0,
            filters: [{ field: "scope", op: "eq", value: "category" }],
          }),
      },
    ],
  });

  const categories = categoriesQ.data?.items ?? [];
  const totalCount = categoriesQ.data?.total ?? 0;
  const rulesM = rulesMq.data?.data ?? [];
  const rulesMech = rulesMechQ.data?.data ?? [];

  const byScopeId = (list: FeeRuleDTO[]): Map<string, FeeRuleDTO> =>
    new Map(list.filter((r) => r.scopeId).map((r) => [r.scopeId as string, r]));

  const mapM = byScopeId(rulesM);
  const mapMech = byScopeId(rulesMech);

  const rows: CategoryRow[] = categories.map((c) => ({
    categoryId: c.id,
    categoryName: c.name,
    marketplace: mapM.get(c.id) ?? null,
    mechanic: mapMech.get(c.id) ?? null,
  }));

  const isLoading =
    categoriesQ.isLoading || rulesMq.isLoading || rulesMechQ.isLoading;

  const columns: TanstackReactTable.ColumnDef<CategoryRow>[] = [
    {
      id: "category",
      header: "ຫມວດຫມູ່",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.categoryName}</span>
      ),
      size: 220,
    },
    {
      id: "marketplace",
      header: "ຄ່າຄອມ Marketplace",
      cell: ({ row }) => {
        const r = row.original.marketplace;
        if (!r)
          return canManage ? (
            <button
              type="button"
              onClick={() =>
                onAddForCategory(
                  row.original.categoryId,
                  row.original.categoryName,
                )
              }
              className="inline-flex items-center gap-1 text-secondary text-sm hover:underline"
            >
              <PlusIcon className="size-4" aria-hidden />
              ຕັ້ງຄ່າ
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        return (
          <div className="flex items-center gap-2">
            <span>{formatValue(r)}</span>
            {r.isActive ? (
              <Badge variant="success" className="text-xs">
                ໃຊ້ງານ
              </Badge>
            ) : (
              <Badge variant="destructive" className="text-xs">
                ປິດ
              </Badge>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => onEdit(r)}
                className="inline-flex items-center gap-1 text-secondary text-sm hover:underline"
              >
                <EditIcon className="size-4" aria-hidden />
                ແກ້ໄຂ
              </button>
            )}
          </div>
        );
      },
      size: 260,
    },
    {
      id: "mechanic",
      header: "ຄ່າທຳນຽມງານຊ່ງ",
      cell: ({ row }) => {
        const r = row.original.mechanic;
        if (!r)
          return canManage ? (
            <button
              type="button"
              onClick={() =>
                onAddForCategory(
                  row.original.categoryId,
                  row.original.categoryName,
                )
              }
              className="inline-flex items-center gap-1 text-secondary text-sm hover:underline"
            >
              <PlusIcon className="size-4" aria-hidden />
              ຕັ້ງຄ່າ
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        return (
          <div className="flex items-center gap-2">
            <span>{formatValue(r)}</span>
            {r.isActive ? (
              <Badge variant="success" className="text-xs">
                ໃຊ້ງານ
              </Badge>
            ) : (
              <Badge variant="destructive" className="text-xs">
                ປິດ
              </Badge>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => onEdit(r)}
                className="inline-flex items-center gap-1 text-secondary text-sm hover:underline"
              >
                <EditIcon className="size-4" aria-hidden />
                ແກ້ໄຂ
              </button>
            )}
          </div>
        );
      },
      size: 260,
    },
  ];

  return (
    <DataTable<CategoryRow, unknown>
      noDataMessage="ບໍ່ພົບຫມວດຫມູ່"
      isLoading={isLoading}
      columns={columns}
      data={rows}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
